// Signature builder → HTML generator with multiple layouts.
// All output uses inline-styled tables (Outlook-safe, no flexbox/grid).

export const SOCIAL_PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", icon: "linkedin" },
  { key: "twitter", label: "X / Twitter", icon: "twitterx" },
  { key: "facebook", label: "Facebook", icon: "facebook-new" },
  { key: "instagram", label: "Instagram", icon: "instagram-new" },
  { key: "youtube", label: "YouTube", icon: "youtube-play" },
  { key: "tiktok", label: "TikTok", icon: "tiktok" },
  { key: "whatsapp", label: "WhatsApp", icon: "whatsapp" },
  { key: "pinterest", label: "Pinterest", icon: "pinterest" },
];

export const LAYOUTS = [
  { key: "side-divider", label: "Side · Divider", desc: "Logo left, vertical bar, info right" },
  { key: "aurora", label: "Aurora", desc: "Bold name + title left, logo right, horizontal divider, labeled rows" },
  { key: "stacked", label: "Stacked", desc: "Logo top, info centered below" },
  { key: "bold-header", label: "Bold Header", desc: "Coloured banner top with name, info below" },
  { key: "card", label: "Accent Card", desc: "Left-accent bar, compact contact, social row" },
  { key: "minimal", label: "Minimal", desc: "Two lines — name & contact" },
];

export const FONTS = [
  { key: "Arial", label: "Arial (universal)" },
  { key: "'Helvetica Neue', Helvetica, Arial, sans-serif", label: "Helvetica" },
  { key: "Verdana", label: "Verdana" },
  { key: "Tahoma", label: "Tahoma" },
  { key: "'Trebuchet MS', sans-serif", label: "Trebuchet MS" },
  { key: "Georgia, serif", label: "Georgia (serif)" },
];

export const defaultBuilderState = () => ({
  layout: "aurora",
  brandColor: "#0d4f8a",
  accentColor: "#10b981",
  textColor: "#1f2937",
  mutedColor: "#6b7280",
  fontFamily: "Arial",
  fontSize: 13,
  logoWidth: 130,
  logoUrl: "{{logoUrl}}",
  show: {
    jobTitle: true, department: false,
    company: true, address: true,
    mobile: true, businessPhone: true,
    email: true, website: true,
    socials: true, disclaimer: true, banner: false,
  },
  labels: { mobile: "M", businessPhone: "T", email: "E", website: "W" },
  socials: [
    { platform: "linkedin", url: "" },
    { platform: "facebook", url: "" },
  ],
  bannerUrl: "",
  bannerHref: "",
  disclaimer: "{{tagline}}",
});

function socialIconUrl(platform, hexColor) {
  const def = SOCIAL_PLATFORMS.find(s => s.key === platform);
  const ic = def?.icon || platform;
  const c = (hexColor || "0d4f8a").replace("#", "").toLowerCase();
  return `https://img.icons8.com/ios-filled/40/${c}/${ic}.png`;
}

function escapeAttr(s) {
  return String(s || "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Renders the social icon row HTML (no spacing wrapper). Returns "" if disabled or empty.
function renderSocials(s, color) {
  if (!s.show.socials) return "";
  const items = (s.socials || []).filter(x => x.platform && x.url);
  if (!items.length) return "";
  const cells = items.map(x =>
    `<td style="padding-right:6px;"><a href="${escapeAttr(x.url)}" style="text-decoration:none;"><img src="${socialIconUrl(x.platform, color)}" alt="${escapeAttr(x.platform)}" width="22" height="22" style="display:block;border:0;width:22px;height:22px;" /></a></td>`
  ).join("");
  return `<table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr>${cells}</tr></table>`;
}

function renderDisclaimer(s) {
  if (!s.show.disclaimer || !s.disclaimer) return "";
  const style = `font-family:${s.fontFamily}; font-size:${Math.round(s.fontSize * 0.82)}px; color:${s.mutedColor}; line-height:1.4; max-width:540px; margin-top:10px;`;
  return `<div style="${style}">${s.disclaimer}</div>`;
}

function renderBanner(s) {
  if (!s.show.banner || !s.bannerUrl) return "";
  const img = `<img src="${escapeAttr(s.bannerUrl)}" alt="banner" width="560" style="display:block; width:560px; max-width:100%; height:auto; border:0; margin-top:12px;" />`;
  return s.bannerHref ? `<a href="${escapeAttr(s.bannerHref)}" style="text-decoration:none;">${img}</a>` : img;
}

// ---------- LAYOUT 1: side-divider ----------
function renderSideDivider(s) {
  const lineStyle = `font-family:${s.fontFamily}; font-size:${s.fontSize}px; color:${s.textColor}; line-height:1.5;`;
  const labelStyle = `color:${s.brandColor}; font-weight:700;`;
  const name = `<div style="font-family:${s.fontFamily}; font-size:${Math.round(s.fontSize * 1.3)}px; font-weight:700; color:${s.brandColor};">{{displayName}}</div>`;
  const title = s.show.jobTitle ? `<div style="font-family:${s.fontFamily}; font-size:${s.fontSize}px; color:${s.mutedColor}; margin-top:2px;">{{jobTitle}}${s.show.department ? ` · {{department}}` : ""}</div>` : "";
  const companyBlock = [];
  if (s.show.company) companyBlock.push(`<strong>{{companyName}}</strong>`);
  if (s.show.address) companyBlock.push(`{{companyAddress}}`);
  const company = companyBlock.length ? `<div style="${lineStyle} margin-top:8px;">${companyBlock.join("<br/>")}</div>` : "";
  const contact = [];
  if (s.show.mobile) contact.push(`<span style="${labelStyle}">${s.labels.mobile}</span> {{mobilePhone}}`);
  if (s.show.businessPhone) contact.push(`<span style="${labelStyle}">${s.labels.businessPhone}</span> {{companyPhone}}`);
  if (s.show.email) contact.push(`<span style="${labelStyle}">${s.labels.email}</span> <a href="mailto:{{mail}}" style="color:${s.textColor};text-decoration:none;">{{mail}}</a>`);
  if (s.show.website) contact.push(`<span style="${labelStyle}">${s.labels.website}</span> <a href="{{companyWebsite}}" style="color:${s.textColor};text-decoration:none;">{{companyWebsite}}</a>`);
  const contactBlock = contact.length ? `<div style="${lineStyle} margin-top:8px;">${contact.join('<br/>')}</div>` : "";
  const socials = renderSocials(s, s.brandColor);
  const socialsBlock = socials ? `<div style="margin-top:10px;">${socials}</div>` : "";
  const logoCell = s.logoUrl ? `<td style="padding-right:18px; border-right:3px solid ${s.brandColor}; vertical-align:top;">
    <img src="${s.logoUrl}" alt="logo" width="${s.logoWidth}" style="display:block; max-width:${s.logoWidth}px; height:auto; border:0;" />
  </td>` : "";
  const padLeft = s.logoUrl ? "padding-left:18px;" : "";
  return `<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="${lineStyle}"><tr>${logoCell}<td style="${padLeft} vertical-align:top;">${name}${title}${company}${contactBlock}${socialsBlock}</td></tr></table>${renderBanner(s)}${renderDisclaimer(s)}`;
}

// ---------- LAYOUT 2: Aurora (matches user's reference signature) ----------
function renderAurora(s) {
  const baseStyle = `font-family:${s.fontFamily}; color:${s.textColor};`;
  const nameStyle = `font-family:${s.fontFamily}; font-size:${Math.round(s.fontSize * 2.2)}px; font-weight:900; color:${s.brandColor}; line-height:1.1; letter-spacing:-0.5px;`;
  const titleStyle = `font-family:${s.fontFamily}; font-size:${Math.round(s.fontSize * 1.15)}px; color:${s.accentColor}; font-style:normal; line-height:1.3; margin-top:4px; max-width:380px;`;
  const labelStyle = `font-family:${s.fontFamily}; font-size:${s.fontSize}px; color:${s.accentColor}; font-weight:700; padding-right:14px; white-space:nowrap;`;
  const valueStyle = `font-family:${s.fontFamily}; font-size:${s.fontSize}px; color:${s.textColor}; line-height:1.6;`;

  // labeled rows
  const rows = [];
  if (s.show.email) rows.push(`<tr><td style="${labelStyle}">Email:</td><td style="${valueStyle}"><a href="mailto:{{mail}}" style="color:${s.textColor};text-decoration:none;">{{mail}}</a></td></tr>`);
  if (s.show.businessPhone) rows.push(`<tr><td style="${labelStyle}">Tel:</td><td style="${valueStyle}">{{companyPhone}}</td></tr>`);
  if (s.show.mobile) rows.push(`<tr><td style="${labelStyle}">Mob:</td><td style="${valueStyle}">{{mobilePhone}}</td></tr>`);
  if (s.show.website) rows.push(`<tr><td style="${labelStyle}">Web:</td><td style="${valueStyle}"><a href="{{companyWebsite}}" style="color:${s.textColor};text-decoration:none;">{{companyWebsite}}</a></td></tr>`);
  if (s.show.address) rows.push(`<tr><td style="${labelStyle}">Address:</td><td style="${valueStyle}">{{companyAddress}}</td></tr>`);
  const rowsTable = rows.length ? `<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin-top:14px;">${rows.join("")}</table>` : "";

  const logoCell = s.logoUrl
    ? `<td valign="middle" align="right" style="padding-left:16px;"><img src="${s.logoUrl}" alt="logo" width="${s.logoWidth}" style="display:block; max-width:${s.logoWidth}px; height:auto; border:0;" /></td>`
    : "";

  const title = s.show.jobTitle ? `<div style="${titleStyle}">{{jobTitle}}${s.show.department ? ` <span style="color:${s.mutedColor};">·</span> {{department}}` : ""}</div>` : "";
  const socials = renderSocials(s, s.brandColor);
  const socialsBlock = socials ? `<div style="margin-top:12px;">${socials}</div>` : "";

  return `<div style="${baseStyle}">
    <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="width:auto;">
      <tr>
        <td valign="top" style="padding-right:16px;">
          <div style="${nameStyle}">{{displayName}}</div>
          ${title}
        </td>
        ${logoCell}
      </tr>
      <tr>
        <td colspan="${logoCell ? 2 : 1}" style="border-top:2px solid ${s.accentColor}; padding-top:8px; font-size:1px; line-height:1px;">&nbsp;</td>
      </tr>
      <tr>
        <td colspan="${logoCell ? 2 : 1}">${rowsTable}${socialsBlock}</td>
      </tr>
    </table>
    ${renderBanner(s)}
    ${renderDisclaimer(s)}
  </div>`;
}

// ---------- LAYOUT 3: Stacked (centered) ----------
function renderStacked(s) {
  const base = `font-family:${s.fontFamily}; color:${s.textColor}; font-size:${s.fontSize}px; line-height:1.5; text-align:center;`;
  const name = `<div style="font-size:${Math.round(s.fontSize * 1.6)}px; font-weight:800; color:${s.brandColor}; letter-spacing:0.5px;">{{displayName}}</div>`;
  const title = s.show.jobTitle ? `<div style="font-size:${s.fontSize}px; color:${s.mutedColor}; margin-top:4px; text-transform:uppercase; letter-spacing:1px;">{{jobTitle}}</div>` : "";
  const sep = `<div style="height:2px; width:60px; background:${s.accentColor}; margin:14px auto;"></div>`;
  const contact = [];
  if (s.show.mobile) contact.push(`<span style="color:${s.brandColor};font-weight:700;">${s.labels.mobile}</span> {{mobilePhone}}`);
  if (s.show.businessPhone) contact.push(`<span style="color:${s.brandColor};font-weight:700;">${s.labels.businessPhone}</span> {{companyPhone}}`);
  if (s.show.email) contact.push(`<a href="mailto:{{mail}}" style="color:${s.textColor};text-decoration:none;">{{mail}}</a>`);
  if (s.show.website) contact.push(`<a href="{{companyWebsite}}" style="color:${s.textColor};text-decoration:none;">{{companyWebsite}}</a>`);
  const contactRow = contact.length ? `<div>${contact.join(' &nbsp;·&nbsp; ')}</div>` : "";
  const company = s.show.company || s.show.address ? `<div style="margin-top:6px; color:${s.mutedColor};">${s.show.company ? "<strong>{{companyName}}</strong>" : ""}${s.show.company && s.show.address ? " · " : ""}${s.show.address ? "{{companyAddress}}" : ""}</div>` : "";
  const logo = s.logoUrl ? `<div style="margin-bottom:12px;"><img src="${s.logoUrl}" alt="logo" width="${s.logoWidth}" style="display:inline-block; max-width:${s.logoWidth}px; height:auto; border:0;" /></div>` : "";
  const socials = renderSocials(s, s.brandColor);
  const socialsBlock = socials ? `<div style="margin-top:12px; display:inline-block;">${socials}</div>` : "";
  return `<div style="${base}">${logo}${name}${title}${sep}${contactRow}${company}${socialsBlock}</div>${renderBanner(s)}${renderDisclaimer(s)}`;
}

// ---------- LAYOUT 4: Bold Header (coloured top bar) ----------
function renderBoldHeader(s) {
  const headerColor = s.brandColor;
  const headerTextColor = "#ffffff";
  const base = `font-family:${s.fontFamily}; color:${s.textColor}; font-size:${s.fontSize}px;`;
  const headerInner = `<table cellpadding="0" cellspacing="0" border="0" role="presentation" width="100%">
    <tr>
      <td style="padding:14px 18px; vertical-align:middle;">
        <div style="color:${headerTextColor}; font-size:${Math.round(s.fontSize * 1.5)}px; font-weight:800;">{{displayName}}</div>
        ${s.show.jobTitle ? `<div style="color:rgba(255,255,255,0.85); font-size:${s.fontSize}px; margin-top:2px;">{{jobTitle}}${s.show.department ? ` · {{department}}` : ""}</div>` : ""}
      </td>
      ${s.logoUrl ? `<td align="right" style="padding:14px 18px; vertical-align:middle;"><img src="${s.logoUrl}" alt="logo" width="${s.logoWidth}" style="display:block; max-width:${s.logoWidth}px; height:auto; border:0;" /></td>` : ""}
    </tr>
  </table>`;
  const contact = [];
  if (s.show.mobile) contact.push(`<span style="color:${s.brandColor};font-weight:700;">${s.labels.mobile}</span> {{mobilePhone}}`);
  if (s.show.businessPhone) contact.push(`<span style="color:${s.brandColor};font-weight:700;">${s.labels.businessPhone}</span> {{companyPhone}}`);
  if (s.show.email) contact.push(`<span style="color:${s.brandColor};font-weight:700;">${s.labels.email}</span> <a href="mailto:{{mail}}" style="color:${s.textColor};text-decoration:none;">{{mail}}</a>`);
  if (s.show.website) contact.push(`<span style="color:${s.brandColor};font-weight:700;">${s.labels.website}</span> <a href="{{companyWebsite}}" style="color:${s.textColor};text-decoration:none;">{{companyWebsite}}</a>`);
  const companyLine = s.show.company || s.show.address ? `<div style="margin-bottom:6px; color:${s.textColor};">${s.show.company ? "<strong>{{companyName}}</strong>" : ""}${s.show.company && s.show.address ? " · " : ""}${s.show.address ? "{{companyAddress}}" : ""}</div>` : "";
  const contactBlock = contact.length ? `<div style="line-height:1.6;">${contact.join('<br/>')}</div>` : "";
  const socials = renderSocials(s, s.brandColor);
  const socialsBlock = socials ? `<div style="margin-top:10px;">${socials}</div>` : "";
  return `<div style="${base} max-width:580px;">
    <div style="background:${headerColor}; border-radius:6px 6px 0 0;">${headerInner}</div>
    <div style="background:#f8fafc; padding:14px 18px; border-radius:0 0 6px 6px;">${companyLine}${contactBlock}${socialsBlock}</div>
  </div>${renderBanner(s)}${renderDisclaimer(s)}`;
}

// ---------- LAYOUT 5: Accent card ----------
function renderCard(s) {
  const base = `font-family:${s.fontFamily}; color:${s.textColor}; font-size:${s.fontSize}px; line-height:1.5;`;
  const labelStyle = `color:${s.brandColor}; font-weight:700;`;
  const cardInner = `<table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr>
    <td style="border-left:4px solid ${s.brandColor}; padding-left:14px; vertical-align:top;">
      <div style="font-size:${Math.round(s.fontSize * 1.4)}px; font-weight:800; color:${s.brandColor};">{{displayName}}</div>
      ${s.show.jobTitle ? `<div style="color:${s.mutedColor}; font-size:${s.fontSize}px; margin-top:2px;">{{jobTitle}}${s.show.department ? ` · {{department}}` : ""}</div>` : ""}
      ${(s.show.company || s.show.address) ? `<div style="margin-top:8px;">${s.show.company ? "<strong>{{companyName}}</strong>" : ""}${s.show.company && s.show.address ? "<br/>" : ""}${s.show.address ? "{{companyAddress}}" : ""}</div>` : ""}
      <div style="margin-top:8px;">
        ${[
          s.show.mobile ? `<span style="${labelStyle}">${s.labels.mobile}</span> {{mobilePhone}}` : "",
          s.show.businessPhone ? `<span style="${labelStyle}">${s.labels.businessPhone}</span> {{companyPhone}}` : "",
          s.show.email ? `<a href="mailto:{{mail}}" style="color:${s.textColor};text-decoration:none;"><span style="${labelStyle}">${s.labels.email}</span> {{mail}}</a>` : "",
          s.show.website ? `<a href="{{companyWebsite}}" style="color:${s.textColor};text-decoration:none;"><span style="${labelStyle}">${s.labels.website}</span> {{companyWebsite}}</a>` : "",
        ].filter(Boolean).join('<br/>')}
      </div>
      ${renderSocials(s, s.brandColor) ? `<div style="margin-top:10px;">${renderSocials(s, s.brandColor)}</div>` : ""}
    </td>
    ${s.logoUrl ? `<td valign="top" style="padding-left:18px;"><img src="${s.logoUrl}" alt="logo" width="${s.logoWidth}" style="display:block; max-width:${s.logoWidth}px; height:auto; border:0;" /></td>` : ""}
  </tr></table>`;
  return `<div style="${base}">${cardInner}</div>${renderBanner(s)}${renderDisclaimer(s)}`;
}

// ---------- LAYOUT 6: Minimal ----------
function renderMinimal(s) {
  const base = `font-family:${s.fontFamily}; color:${s.textColor}; font-size:${s.fontSize}px; line-height:1.5;`;
  const line1 = `<div><strong style="color:${s.brandColor}; font-size:${Math.round(s.fontSize * 1.15)}px;">{{displayName}}</strong>${s.show.jobTitle ? ` <span style="color:${s.mutedColor};"> · {{jobTitle}}</span>` : ""}${s.show.company ? ` <span style="color:${s.mutedColor};"> · {{companyName}}</span>` : ""}</div>`;
  const contact = [];
  if (s.show.mobile) contact.push(`{{mobilePhone}}`);
  if (s.show.email) contact.push(`<a href="mailto:{{mail}}" style="color:${s.textColor};text-decoration:none;">{{mail}}</a>`);
  if (s.show.website) contact.push(`<a href="{{companyWebsite}}" style="color:${s.textColor};text-decoration:none;">{{companyWebsite}}</a>`);
  const line2 = contact.length ? `<div style="color:${s.mutedColor}; margin-top:2px;">${contact.join(' · ')}</div>` : "";
  const socials = renderSocials(s, s.brandColor);
  const socialsBlock = socials ? `<div style="margin-top:8px;">${socials}</div>` : "";
  return `<div style="${base}">${line1}${line2}${socialsBlock}</div>${renderBanner(s)}${renderDisclaimer(s)}`;
}

const RENDERERS = {
  "side-divider": renderSideDivider,
  "aurora": renderAurora,
  "stacked": renderStacked,
  "bold-header": renderBoldHeader,
  "card": renderCard,
  "minimal": renderMinimal,
};

export function generateSignatureHtml(state) {
  const def = defaultBuilderState();
  const s = {
    ...def,
    ...state,
    show: { ...def.show, ...(state?.show || {}) },
    labels: { ...def.labels, ...(state?.labels || {}) },
  };
  const fn = RENDERERS[s.layout] || renderSideDivider;
  return fn(s);
}
